import { type TemplateField } from '@/components/features/ServiceRequests/DynamicRequestForm';
import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { useTranslations } from '@/hooks/useTranslations';
import { ArrowDown, ArrowUp, ShieldX, Trash2 } from 'lucide-react';

export interface ApprovalStep {
    label: string;
    role: string;
    triggers_access_revocation: boolean;
}

const NONE = '__none__';

export interface ApprovalConfig {
    requires_approval: boolean;
    approval_steps: ApprovalStep[];
    final_notify_role: string;
    final_notify_user_id: string;
    subject_field_key: string;
}

interface ApprovalStepsEditorProps {
    value: ApprovalConfig;
    fields: TemplateField[];
    roles: string[];
    users: Array<{ id: string; name: string }>;
    onChange: (patch: Partial<ApprovalConfig>) => void;
}

export function ApprovalStepsEditor({ value, fields, roles, users, onChange }: ApprovalStepsEditorProps) {
    const { t } = useTranslations();
    const steps = value.approval_steps ?? [];
    const userPickerFields = fields.filter((f) => f.type === 'user_picker');

    const updateStep = (index: number, patch: Partial<ApprovalStep>) => {
        const next = steps.map((s, i) => (i === index ? { ...s, ...patch } : s));
        onChange({ approval_steps: next });
    };

    const addStep = () => {
        onChange({
            approval_steps: [...steps, { label: '', role: roles[0] ?? '', triggers_access_revocation: false }],
        });
    };

    const removeStep = (index: number) => {
        onChange({ approval_steps: steps.filter((_, i) => i !== index) });
    };

    const moveStep = (index: number, dir: -1 | 1) => {
        const target = index + dir;
        if (target < 0 || target >= steps.length) return;
        const next = [...steps];
        [next[index], next[target]] = [next[target], next[index]];
        onChange({ approval_steps: next });
    };

    const revocationStepIndex = steps.findIndex((s) => s.triggers_access_revocation);
    const revocationNotLast = revocationStepIndex !== -1 && revocationStepIndex !== steps.length - 1;

    return (
        <div className="space-y-4">
            <div className="flex items-start gap-2">
                <Checkbox
                    id="requires_approval"
                    checked={value.requires_approval}
                    onCheckedChange={(v) => onChange({ requires_approval: Boolean(v) })}
                />
                <div>
                    <Label htmlFor="requires_approval" className="cursor-pointer">
                        {t('service_requests.signoff.requires_approval', 'Require sign-off before completion')}
                    </Label>
                    <p className="text-muted-foreground text-xs">
                        {t(
                            'service_requests.signoff.requires_approval_help',
                            'Submissions of this form must be signed off, in order, by the roles below.',
                        )}
                    </p>
                </div>
            </div>

            {value.requires_approval && (
                <div className="space-y-4 border-l-2 pl-4">
                    <div className="space-y-3">
                        {steps.length === 0 && (
                            <p className="text-muted-foreground text-sm">
                                {t('service_requests.signoff.no_steps', 'No steps yet. Add the first signatory below.')}
                            </p>
                        )}

                        {steps.map((step, index) => (
                            <div key={index} className="bg-muted/30 space-y-3 rounded-lg border p-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground text-xs font-semibold">
                                        {t('service_requests.signoff.step', 'Step')} {index + 1}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => moveStep(index, -1)}
                                            disabled={index === 0}
                                        >
                                            <ArrowUp className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => moveStep(index, 1)}
                                            disabled={index === steps.length - 1}
                                        >
                                            <ArrowDown className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="text-destructive h-7 w-7"
                                            onClick={() => removeStep(index)}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <div className="space-y-1.5">
                                        <Label>{t('service_requests.signoff.step_label', 'Label')}</Label>
                                        <Input
                                            value={step.label}
                                            onChange={(e) => updateStep(index, { label: e.target.value })}
                                            placeholder={t('service_requests.signoff.step_label_placeholder', 'e.g. Warehouse clearance')}
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>{t('service_requests.signoff.step_role', 'Signed by role')}</Label>
                                        <Select value={step.role || NONE} onValueChange={(v) => updateStep(index, { role: v === NONE ? '' : v })}>
                                            <SelectTrigger>
                                                <SelectValue placeholder={t('service_requests.signoff.select_role', 'Select role')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {roles.map((r) => (
                                                    <SelectItem key={r} value={r}>
                                                        {r}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="flex items-start gap-2">
                                    <Checkbox
                                        id={`revoke-${index}`}
                                        checked={step.triggers_access_revocation}
                                        onCheckedChange={(v) => updateStep(index, { triggers_access_revocation: Boolean(v) })}
                                    />
                                    <Label htmlFor={`revoke-${index}`} className="cursor-pointer text-sm font-normal">
                                        {t(
                                            'service_requests.signoff.triggers_revocation',
                                            'Signing this step requests access revocation for the employee (IT step)',
                                        )}
                                    </Label>
                                </div>
                            </div>
                        ))}

                        <Button type="button" variant="outline" size="sm" onClick={addStep}>
                            {t('service_requests.signoff.add_step', 'Add step')}
                        </Button>

                        {revocationNotLast && (
                            <p className="text-warning-subtle-foreground flex items-center gap-1.5 text-xs">
                                <ShieldX className="h-3.5 w-3.5" />
                                {t('service_requests.signoff.revocation_not_last', 'The access-revocation (IT) step is usually the last one.')}
                            </p>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <Label>{t('service_requests.signoff.subject_field', 'Employee field')}</Label>
                        <Select value={value.subject_field_key || NONE} onValueChange={(v) => onChange({ subject_field_key: v === NONE ? '' : v })}>
                            <SelectTrigger>
                                <SelectValue placeholder={t('service_requests.signoff.subject_field_placeholder', 'Which field is the employee?')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NONE}>{t('service_requests.signoff.none', 'None')}</SelectItem>
                                {userPickerFields.map((f) => (
                                    <SelectItem key={f.key} value={f.key}>
                                        {f.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-muted-foreground text-xs">
                            {t(
                                'service_requests.signoff.subject_field_help',
                                'The user-picker field naming the departing employee — used for access-revocation groundwork.',
                            )}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label>{t('service_requests.signoff.final_notify_role', 'Notify role when complete')}</Label>
                            <Select
                                value={value.final_notify_role || NONE}
                                onValueChange={(v) => onChange({ final_notify_role: v === NONE ? '' : v })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t('service_requests.signoff.none', 'None')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={NONE}>{t('service_requests.signoff.none', 'None')}</SelectItem>
                                    {roles.map((r) => (
                                        <SelectItem key={r} value={r}>
                                            {r}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t('service_requests.signoff.final_notify_user', 'Notify user when complete')}</Label>
                            <Select
                                value={value.final_notify_user_id || NONE}
                                onValueChange={(v) => onChange({ final_notify_user_id: v === NONE ? '' : v })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t('service_requests.signoff.none', 'None')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={NONE}>{t('service_requests.signoff.none', 'None')}</SelectItem>
                                    {users.map((u) => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
